import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef } from 'react';
import { useGetPartnerAdminsGroups, usePutPartnerAdminsTopBanner } from '~/hooks';
import { generateRequest } from '../logics/generateRequest';
import { EditTopBannerFormFieldValues, editTopBannerSchema } from '../logics/schema';
import { EditTopBannerFormPresentation } from '../presentations';
import { useGetPartnerAdminsTopBanner } from './useGetPartnerAdminsTopBanner';

type PresentationProps = React.ComponentProps<typeof EditTopBannerFormPresentation>;

type IUseEditTopBannerForm = (topBannerId: number) => {
  state: {
    methods: UseFormReturn<EditTopBannerFormFieldValues>;
    data: ReturnType<typeof useGetPartnerAdminsTopBanner>['data'];
    groupsData: ReturnType<typeof useGetPartnerAdminsGroups>['data'];
    isImageRemovedRef: React.MutableRefObject<boolean>;
    isMutating: boolean;
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
    handleImageRemove: PresentationProps['onImageRemove'];
  };
};

/**
 * トップバナー編集フォームのカスタムフック
 *
 * @param topBannerId - 編集対象のトップバナーID
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.data - APIから取得したトップバナーデータ
 * @returns state.isImageRemovedRef - 既存画像が削除されたかどうかのref
 * @returns state.isMutating - API実行中フラグ
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 * @returns actions.handleImageRemove - 画像削除時のコールバック
 */
export const useEditTopBannerForm: IUseEditTopBannerForm = (topBannerId: number) => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();
  // 既存画像が削除されたかどうかを追跡（初期状態は削除されていない）
  const isImageRemovedRef = useRef(false);

  const { data } = useGetPartnerAdminsTopBanner({
    threeLetterCode,
    topBannerId,
  });

  const { data: groupsData } = useGetPartnerAdminsGroups({ threeLetterCode });

  const topBanner = data.top_banner;

  const methods = useForm<EditTopBannerFormFieldValues>({
    defaultValues: {
      title: topBanner.title,
      transitionUrl: topBanner.transition_url,
      restrictedToApp: topBanner.restricted_to_app ? 'true' : 'false',
      status: topBanner.status === 'active' ? '0' : '1',
      // Fileがあれば更新、undefinedなら既存画像維持
      // 既存画像の表示はImageInputのdefaultPreviewUrlで制御
      image: undefined,
      groupId: topBanner.group?.id ? String(topBanner.group.id) : undefined,
      targetUserType: topBanner.target_user_type ?? 'everyone',
    },
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(editTopBannerSchema),
  });

  const { trigger, isMutating } = usePutPartnerAdminsTopBanner(threeLetterCode, topBannerId, {
    onSuccess: () => {
      showToast({
        message: 'バナーの更新に成功しました。',
        status: 'success',
      });
      navigate('/top-banners');
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'バナーの更新に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: 'バナーの更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(async formValues => {
    const requestBody = await generateRequest({
      formValues,
      existingTopBanner: topBanner,
    });
    await trigger(requestBody);
  });

  // 画像削除時に呼ばれるコールバック
  const handleImageRemove = () => {
    isImageRemovedRef.current = true;
  };

  // 新しい画像がアップロードされたら削除フラグをリセット
  // （削除後に再度画像を選択した場合、有効状態に戻す）
  const imageValue = methods.watch('image');
  if (imageValue instanceof File) {
    isImageRemovedRef.current = false;
  }

  return {
    state: {
      methods,
      data,
      groupsData,
      isImageRemovedRef,
      isMutating,
    },
    actions: {
      handleOnSubmit,
      handleImageRemove,
    },
  };
};
